import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTypedSelector } from 'src/State/RootReducer'
import useModals from 'src/Hooks/useModals'
import { Components, Product } from 'src/Libs/shop/models/system-configuration-components.model'
import { addProduct, removeProduct } from 'src/State/ShopQntmSystemConfiguration/ShopQntmSystemConfigurationAction'
import { ProductType, ProgressBarSteps } from 'src/Libs/shop/types'
import { SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM } from 'src/Libs/shop/consts'
import { useEffectShopAccess } from 'src/Libs/shop/hooks'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useQntmSystemConfigurationContext } from 'src/Libs/shop/context/use-shop-qntm-system-configuration-context'
import useShopQntmSystemConfiguration from 'src/Libs/shop/hooks/use-qntm-system-configuration'

interface UseQntmMainComponentBehaviorParams {
  title: string
  categories: Components[]
  qntmProduct: Product | undefined
  isLoading: boolean
  onBack: () => void
  onMoreInfo: (productId: string) => void
  onSelectProduct: (category: Components, product: Product) => void
  isProductSelected: (productId: string, categoryId: string) => boolean
  canContinue: boolean
}

/*
 * Handles data and selection logic for the Normal Main Component step
 */
export const useShopQntmMainComponent = (): UseQntmMainComponentBehaviorParams => {
  const { t } = useCustomTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { setAllowContinueMain } = useQntmSystemConfigurationContext()
  const { getQntmSystemConfigurationComponents } = useShopQntmSystemConfiguration()
  const { openShopMoreInfo, openShopHintModal, openShopRetrofitBatteryModal } = useModals()

  const configuratorItems = useTypedSelector(state => state.qntmStorage.items)
  const selectedElement = configuratorItems.length > 0 ? configuratorItems[0].configuration.configuratorId : null
  const selectedComponents = configuratorItems.length > 0 ? configuratorItems[0].configuration.components : null
  const [categories, setCategories] = useState<Components[]>([])
  const [qntmProduct, setQntmProduct] = useState<Product | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    if (!selectedElement) {
      navigate(location.pathname + `?${SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM}=${ProgressBarSteps.BasicSystem}`, { replace: true })
    }
  }, [selectedElement, navigate, location.pathname])

  useEffectShopAccess(async () => {
    if (!selectedElement) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const result = await getQntmSystemConfigurationComponents(selectedElement)
      const filteredCategories =
        result?.components?.filter(category => category.products.length > 0 && category.mandatory === true).sort((a, b) => a.position - b.position) ??
        []

      setCategories(filteredCategories)
      setQntmProduct(result?.em360Product)
    } finally {
      setIsLoading(false)
    }
  }, [selectedElement])
  const isProductSelected = (productId: string, categoryId: string): boolean => {
    return Boolean(
      selectedComponents?.some(
        component => component.id === categoryId && component.products.some(selectedProduct => selectedProduct.id === productId)
      )
    )
  }

  const canContinue = useMemo(() => {
    const mandatoryCategories = categories.filter(category => category.mandatory)

    return mandatoryCategories.every(category => {
      return Boolean(selectedComponents?.some(component => component.id === category.id && component.products.length > 0))
    })
  }, [categories, selectedComponents])

  useEffect(() => {
    setAllowContinueMain?.(canContinue)
  }, [canContinue, setAllowContinueMain])

  const onMoreInfo = (productId: string): void => {
    openShopMoreInfo({ productId })
  }

  const onSelectProduct = (categoryData: Components, product: Product): void => {
    const selectedCategoryIndex = selectedComponents?.findIndex(component => component.id === categoryData.id)
    const oldProductId =
      selectedComponents && selectedCategoryIndex !== undefined && selectedCategoryIndex >= 0
        ? selectedComponents[selectedCategoryIndex].products[0]?.id
        : null
    const productHint =
      product.customFields.e3dcPopupActive && product.customFields.e3dcPopupInfoText ? product.customFields.e3dcPopupInfoText : false
    const isSelected = isProductSelected(product.id, categoryData.id)
    if (isSelected) {
      if (oldProductId != null) {
        dispatch(removeProduct(categoryData.id, oldProductId))
      }
      return
    }
    if (oldProductId != null) {
      dispatch(removeProduct(categoryData.id, oldProductId))
    }
    dispatch(addProduct(categoryData.id, product.id, 1))
    if (productHint) {
      openShopHintModal({ text: productHint })
    }
    if (
      product.extensions?.e3dcData.productType === ProductType.SystemBase &&
      product.extensions.e3dcData.customFields?.retrofitBatteryProductId != null
    ) {
      openShopRetrofitBatteryModal({
        batteryId: product.extensions.e3dcData.customFields.retrofitBatteryProductId,
        min: product.extensions.e3dcData.customFields.min || 0,
        max: product.extensions.e3dcData.customFields.max || 1,
      })
    }
  }

  const onBack = (): void => {
    navigate(location.pathname + `?${SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM}=${ProgressBarSteps.BasicSystem}`, { replace: true })
  }

  return {
    title: t('shop.main-component.title'),
    categories,
    qntmProduct,
    isLoading,
    onBack,
    onMoreInfo,
    onSelectProduct,
    isProductSelected,
    canContinue,
  }
}
